import { computed, ref } from 'vue';
import { apiRequest } from '@/composables/useApi';
import type { User } from '@/types/domain';

interface AuthResponse {
  user: User | null;
  needsAdminSetup: boolean;
}

interface LoginInput {
  email: string;
  password: string;
}

interface RegisterInput extends LoginInput {
  firstName: string;
  lastName: string;
  familyName: string;
  passwordRepeat: string;
}

interface SetupAdminInput extends LoginInput {
  firstName: string;
  lastName: string;
  passwordRepeat: string;
}

interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
}

const user = ref<User | null>(null);
const needsAdminSetup = ref(false);
const isLoading = ref(false);
const isInitialized = ref(false);
const authError = ref('');

export function useAuth() {
  const isAuthenticated = computed(() => Boolean(user.value));

  async function initialize() {
    if (isInitialized.value) return;
    isLoading.value = true;
    authError.value = '';
    try {
      const response = await apiRequest<AuthResponse>('/auth/me');
      user.value = response.user;
      needsAdminSetup.value = response.needsAdminSetup;
    } catch {
      user.value = null;
      needsAdminSetup.value = false;
      authError.value = 'Die Anmeldung ist gerade nicht erreichbar.';
    } finally {
      isInitialized.value = true;
      isLoading.value = false;
    }
  }

  async function login(input: LoginInput) {
    isLoading.value = true;
    authError.value = '';
    try {
      const response = await apiRequest<{ user: User }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(input)
      });
      user.value = response.user;
      needsAdminSetup.value = false;
      return true;
    } catch (error) {
      authError.value = authMessage(error);
      return false;
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  async function register(input: RegisterInput) {
    isLoading.value = true;
    authError.value = '';
    try {
      await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(input)
      });
      return await login({ email: input.email, password: input.password });
    } catch (error) {
      authError.value = authMessage(error);
      return false;
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  async function setupAdmin(input: SetupAdminInput) {
    isLoading.value = true;
    authError.value = '';
    try {
      await apiRequest('/auth/setup-admin', {
        method: 'POST',
        body: JSON.stringify(input)
      });
      needsAdminSetup.value = false;
      return await login({ email: input.email, password: input.password });
    } catch (error) {
      authError.value = authMessage(error);
      return false;
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  async function logout() {
    isLoading.value = true;
    authError.value = '';
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } finally {
      user.value = null;
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  async function forgotPassword(email: string) {
    authError.value = '';
    await apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async function resetPassword(token: string, password: string, passwordRepeat: string) {
    authError.value = '';
    await apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password, passwordRepeat })
    });
  }

  async function changePassword(input: ChangePasswordInput) {
    authError.value = '';
    await apiRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(input)
    });
  }

  return {
    authError,
    changePassword,
    forgotPassword,
    initialize,
    isAuthenticated,
    isInitialized,
    isLoading,
    login,
    logout,
    needsAdminSetup,
    register,
    resetPassword,
    setupAdmin,
    user
  };
}

function authMessage(error: unknown) {
  const message = error instanceof Error ? error.message : 'auth_failed';
  if (message === 'unauthorized') return 'E-Mail oder Passwort stimmt nicht.';
  if (message === 'weak_password') return 'Das Passwort braucht mindestens 12 Zeichen.';
  if (message === 'password_mismatch') return 'Die Passwoerter stimmen nicht ueberein.';
  if (message === 'missing_required_field') return 'Bitte alle Felder ausfuellen.';
  if (message === 'invalid_email') return 'Bitte gib eine gueltige E-Mail-Adresse ein.';
  if (message === 'email_already_exists') return 'Zu dieser E-Mail gibt es bereits ein Konto.';
  if (message === 'admin_already_exists') return 'Die Ersteinrichtung wurde bereits abgeschlossen.';
  return 'Das hat gerade nicht geklappt. Bitte pruefe deine Eingaben.';
}
